import AuthLink from "@/app/[lang]/_components/AuthLink";
import { tGlob } from "@/app/[lang]/_t-glob/t-glob";
import { createT } from "@/app/[lang]/_utils/create-t";
import Image from "@/components/Image";
import type { Lang } from "@/langs";
import type { PostGridData } from "@/validators/post";
import { Card } from "@borderless/ui/components/card";
import { useCollegeColor } from "@borderless/ui/hooks/use-college-color";
import { cn } from "@borderless/ui/lib/utils";
import { type FC } from "react";
import dictionary from "./t.json";

const t = createT(dictionary);

export const StoryInList: FC<{
  lang: Lang;
  post: PostGridData;
  queryParams?: Record<string, string>;
  className?: string;
  isPromoted?: boolean;
}> = ({ post, className, isPromoted, queryParams, ...props }) => {
  const href = queryParams
    ? `/stories/${post.slug}?${new URLSearchParams(queryParams).toString()}`
    : `/stories/${post.slug}`;

  const { bgColor, shadowColor, foregroundColor, foregroundSubtleColor } =
    useCollegeColor({
      org: {
        logoColorC: post.org.logoColorC,
        logoColorH: post.org.logoColorH,
      },
    });

  return (
    <AuthLink
      href={href}
      style={
        {
          "--story-in-list-bg-color": bgColor,
          "--story-in-list-shadow-color": shadowColor,
          "--foreground": foregroundColor,
          "--subtle": foregroundSubtleColor,
        } as React.CSSProperties
      }
    >
      <Card
        variant="button-bg"
        className={cn(
          "relative flex items-center gap-4 overflow-hidden rounded-2xl border border-border bg-(--story-in-list-bg-color) p-3 text-foreground shadow-[4px_4px_0_var(--story-in-list-shadow-color)] transition-all duration-100 ease-in-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--story-in-list-shadow-color)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none md:gap-8",
          className,
        )}
      >
        {/* manually set max possible height to fill the vertical space */}
        <div className="relative aspect-9/10 h-20 w-auto shrink-0 overflow-hidden rounded-md sm:h-32 md:h-40">
          <Image
            src={post.squareImageUrl}
            alt={post.title}
            fill
            sizes="25vw"
            className="object-cover"
          />
        </div>
        <div className="relative grid flex-1 grid-cols-[100%] grid-rows-[auto_1fr_auto] gap-2 py-2 pe-1 md:py-4 md:pe-4">
          <div className="flex items-center gap-2 md:gap-4">
            <Image
              src={post.org.imageUrl}
              alt={post.org.displayName}
              width={40}
              height={40}
              className="h-6 w-6 shrink-0 rounded-md md:h-10 md:w-10"
            />
            <div className="flex grow items-start">
              <div className="grow">
                <h3 className="line-clamp-1 text-xs leading-tight font-normal sm:text-sm">
                  {post.org.displayName} {post.org.country.emoji}
                </h3>
                <p className="line-clamp-1 text-xs leading-tight font-normal text-subtle sm:text-sm">
                  {post.org.city !== null
                    ? `${post.org.city.displayName}, `
                    : ""}
                  {post.org.country.displayName}
                </p>
              </div>
              {isPromoted && (
                <p className="text-xs font-light text-subtle">promoted</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-start justify-center">
            <h2 className="line-clamp-2 text-base leading-tight font-bold text-ellipsis text-foreground sm:text-xl md:text-2xl">
              {post.title}
            </h2>
          </div>
          <div className="flex h-6 items-end md:h-10">
            <div className="flex items-center gap-2">
              {post.author.imageUrl ? (
                <div className="relative h-3 w-3 shrink-0 grow-0 overflow-hidden rounded-full md:h-6 md:w-6">
                  <Image
                    src={post.author.imageUrl}
                    alt={post.author.username ?? "user image"}
                    fill
                    sizes="3rem" // in case the photo is aspect-video
                    className="object-cover"
                  />
                </div>
              ) : (
                <p className="flex h-3 w-3 items-center justify-center rounded-full bg-accent text-xs md:h-6 md:w-6 md:text-lg">
                  {post.author.emoji ?? "😀"}
                </p>
              )}
              <p className="line-clamp-1 text-xs text-subtle sm:text-sm">
                {t("by {firstName} from {country}", props.lang, false, {
                  firstName:
                    post.author.firstName ??
                    post.author.username ??
                    tGlob("Borderless Member", props.lang, true),
                  country: post.author.fromCountry
                    ? `${post.author.fromCountry.displayName} ${post.author.fromCountry.emoji}`
                    : "??",
                })}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </AuthLink>
  );
};
